import React, { useState } from 'react';
import { Button, Modal } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import ServiceJson from './ServiceJson';
import SchemaMonacoEditor from '../SubComponents/SchemaMonacoEditor';
import { validateSchema } from '../utils/schemaValidation';
import SchemaValidationErrors from '../utils/SchemaValidationErrors';

/**
 * The declaration a consumer writes against. Read-only for everyone; a service
 * owner who is allowed to author schemas gets the editor behind a button.
 */
const ServiceSchemaTab = ({ record, canEdit }: { record: any; canEdit: boolean }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [validated, setValidated] = useState(false);
    const [errors, setErrors] = useState<any>(null);

    const edit = () => {
        setDraft(JSON.stringify(record.schema?.schema ?? {}, null, 2));
        setValidated(false);
        setErrors(null);
        setOpen(true);
    };

    const validate = async () => {
        if (!draft) return;
        const result = await validateSchema(draft, dataProvider);
        if (result.success) {
            notify(result.message || 'Schema validation successful', 'success');
            setErrors(null);
        } else {
            setErrors(result.errors);
            notify(result.message || 'Schema validation failed', 'error');
        }
        setValidated(result.success);
    };

    const save = async () => {
        try {
            await dataProvider.create('services', { data: JSON.parse(draft) });
            notify('Schema updated successfully', 'success');
            setOpen(false);
            refresh();
        } catch (error: any) {
            notify(`Failed to update schema: ${error.message}`, 'error');
        }
    };

    return (
        <div className="svc-tab">
            {canEdit && (
                <div className="svc-tab__bar">
                    <Button size="small" icon={<EditOutlined />} onClick={edit}>
                        Edit schema
                    </Button>
                </div>
            )}

            <ServiceJson data={record.schema?.schema ?? {}} />

            <Modal
                title="Edit schema"
                open={open}
                onCancel={() => setOpen(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="validate" onClick={validate}>
                        Validate schema
                    </Button>,
                    <Button key="save" type="primary" onClick={save} disabled={!validated}>
                        Update schema
                    </Button>,
                ]}
            >
                {errors && (
                    <div style={{ marginBottom: 16 }}>
                        <SchemaValidationErrors errors={errors} />
                    </div>
                )}
                <SchemaMonacoEditor value={draft} onChange={(value) => {
                    setDraft(value || '');
                    setValidated(false);
                    setErrors(null);
                }} />
            </Modal>
        </div>
    );
};

export default ServiceSchemaTab;
